import { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
// prime react
import { Button } from "primereact/button";
// components
import Panel from "../../../components/panel/panel";
import Input from "../../../components/input/Input";
import Select from "../../../components/input/Select";
import CalendarInput from "../../../components/input/CalendarInput";
import { TableAccount } from "./TableAccount";
import { AccountHeader } from "./AccountHeader";
//contexts
import { useFinancialContext } from "../../../context/FinancialContext";
import { useAppContext } from "../../../context/AppContext";
//services
import { postAccountsToReceive } from "../../../services/Financial/postAccountsToReceive";
import { getCostCenter } from "../../../services/CostCenter/getCostCenter";
//utils
import { formatYearDate } from "../../../utils/formatDate";
//hooks
import { useOptionsSelect } from "../../../hooks/useOptionsSelect";
import usePermission from "../../../hooks/usePermission";
import { formatCurrency } from "../../../utils/formatCurrency";
import { Paginator } from "primereact/paginator";

const AccountReceivable = () => {
  const form = useForm({});
  const { errors } = form.formState;

  const { filter, setFilter } = useFinancialContext();
  const { nomenclatures, permissions } = useAppContext();
  usePermission("112");
  const nomenclature = JSON.parse(nomenclatures);

  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState(null);
  const [bank, setBank] = useState(null);
  const [enterpriseLogoBase64, setEnterpriseLogoBase64] = useState(null);

  const [paginatorState, setPaginatorState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    totalRecords: 0,
  });

  const [cost_center, setCostCenter] = useState(null);

  const {
    client,
    enterprise,
    isLoadingClients,
    isLoadingEnterprises,
    currentAccount,
    blocks,
    isLoadingBlocks,
    selectEnterprise,
    selectBlock,
    lands,
    isLoadingLands,
    typePeriodOptions,
  } = useOptionsSelect();

  const { selectedCustomers, setSelectedCustomers } = useFinancialContext();

  const fetchData = useCallback(
    async (page, filters) => {
      setLoading(true);
      const notification = toast.loading("Buscando dados...");

      try {
        const response = await postAccountsToReceive(filters, page);
  const { installments, totals: totalsData } = response;

        const mappedData = installments.data.map((row) => ({
          ...row,
          block: row?.sale?.land?.block?.name
            ? `${row.sale.land.block.name} / ${row.sale.land.name}`
            : "",
          client_name: row?.sale?.client?.name || row?.payer?.name || "",
        }));
        const bank = response.bank;
        setBank(bank);
        // Try to extract enterprise logo from top-level response
        const logoBase64 =
          response?.enterprise_logo_base64 ||
          response?.enterprise_logo ||
          response?.logo_base64 ||
          response?.logo ||
          null;
        setEnterpriseLogoBase64(logoBase64);



        setDataTable(mappedData);
        setTotals(totalsData);

        setPaginatorState((prev) => ({
          ...prev,
          totalRecords: installments.total,
          rows: installments.per_page,
        }));

       

        if (mappedData.length === 0) {
          toast.update(notification, {
            render: "Nenhum resultado encontrado.",
            type: "warning",
            isLoading: false,
            autoClose: 4000,
            theme: "colored",
          });
        } else {
          toast.update(notification, {
            render: "Consulta realizada com sucesso!",
            type: "success",
            isLoading: false,
            autoClose: 2000,
            theme: "colored",
          });
        }
      } catch (error) {
        toast.update(notification, {
          render: "Falha ao buscar dados!",
          type: "error",
          isLoading: false,
          autoClose: 5000,
          theme: "colored",
        });
        console.error("Erro na API:", error);
      } finally {
        setLoading(false);
      }
    },
    [filter, setFilter]
  );

  const onSubmit = (data) => {
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, v]) => v !== undefined && v !== "" && v !== null
      )
    );
    if (cleanedData.status === "all") delete cleanedData.status;
    if (cleanedData.period_start)
      cleanedData.period_start = formatYearDate(cleanedData.period_start);
    if (cleanedData.period_end)
      cleanedData.period_end = formatYearDate(cleanedData.period_end);

    setFilter(cleanedData);
    fetchData(1, cleanedData);
  };

  const handlePageChange = (event) => {
    // console.log("handlePageChange chamado! Evento:", event);
    const nextPage = event.page + 1;
    setPaginatorState((prev) => ({
      ...prev,
      page: nextPage,
      first: event.first,
      rows: event.rows,
    }));
    // Resetar seleções ao mudar de página
    setSelectedCustomers([]);
    fetchData(nextPage, filter);
  };

  useEffect(() => {
    if (filter && Object.keys(filter).length > 0) {
      Object.keys(filter).forEach((key) => {
        form.setValue(key, filter[key]);
      });
      // Resetar seleções antes de carregar novos dados
      setSelectedCustomers([]);
      fetchData(paginatorState.page, filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data } = useQuery({
    queryKey: ["cost-center"],
    queryFn: () => getCostCenter(),
    enabled: !!permissions?.includes("116"),
  });

  useEffect(() => {
    if (data) {
      data.sort((a, b) => {
        return a.ref.localeCompare(b.ref, undefined, { numeric: true });
      });
      const refactorData = data.map((item) => ({
        value: item.id,
        label: `${item.ref} - ${item.desc}`,
      }));
      setCostCenter(refactorData);
    }
  }, [data]);

  const optionCurrent = [
    { value: 0, label: "Em Aberto" },
    { value: 1, label: "Pago" },
  ];
  const optionsShipping = [
    { value: 0, label: "Sem Remessa" },
    { value: 1, label: "Com Remessa" },
  ];
  const optionsAutomatic = [
    { value: 0, label: "Sem Baixa Automática" },
    { value: 1, label: "Com Baixa Automática" },
  ];
  const optionsLawsuit = [
    { value: 0, label: "Sem Ação Judicial" },
    { value: 1, label: "Com Ação Judicial" },
  ];

  const clientId = form.watch("client_id");
  const periodStart = form.watch("period_start");
  const periodEnd = form.watch("period_end");
  const isRequired = !clientId && Boolean(periodStart || periodEnd);

  return (
    <>
      <div className="row d-flex justify-content-between mb-3">
        <AccountHeader
          form={form}
          updatedTable={() => fetchData(1, filter)}
          filter={filter}
          bank={bank}
          enterpriseLogoBase64={enterpriseLogoBase64}
        />
        <Panel title="Informe o Período">
          <form
            className="row d-flex mt-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {permissions?.includes("10") && (
              <div className="col-xl-3 col-md-4 mb-3 form-group">
                <Select
                  name="client_id"
                  control={form.control}
                  label={isLoadingClients ? "Carregando..." : "Cliente"}
                  disabled={isLoadingClients}
                  options={client}
                  errors={errors}
                />
              </div>
            )}
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="enterprise_id"
                label={
                  isLoadingEnterprises ? "Carregando..." : "Empreendimento*"
                }
                control={form.control}
                preValue={filter?.enterprise_id}
                disabled={isLoadingEnterprises}
                isRequired={true}
                onChange={(e) => selectEnterprise(e)}
                options={enterprise}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="block_id"
                label={
                  isLoadingBlocks ? "Carregando..." : nomenclature[0].value
                }
                control={form.control}
                disabled={isLoadingBlocks || !form.getValues("enterprise_id")}
                onChange={(e) => selectBlock(e)}
                options={blocks}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="land_id"
                label={isLoadingLands ? "Carregando..." : nomenclature[1].value}
                control={form.control}
                errors={errors}
                options={lands || ""}
                disabled={isLoadingLands || !form.getValues("block_id")}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="type_period"
                label={`Tipo Período ${isRequired ? "*" : ""}`}
                isRequired={isRequired}
                control={form.control}
                options={typePeriodOptions}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="status"
                label="Situação"
                preValue={filter?.status}
                control={form.control}
                options={optionCurrent}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Select
                name="current_account_id"
                label="Conta"
                control={form.control}
                options={currentAccount}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 form-group">
              <Select
                name="shipping"
                label="Remessa"
                control={form.control}
                options={optionsShipping}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="cost_center_id"
                label={loading ? "Carregando..." : "Centro de Receita"}
                control={form.control}
                errors={errors}
                options={cost_center || ""}
                disabled={loading}
              />
            </div>
            <div className="col-xl-3 col-md-4 form-group">
              <Select
                name="completed_automatic"
                label="Baixa Automática"
                control={form.control}
                options={optionsAutomatic}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 form-group">
              <Select
                name="lawsuit"
                label="Ação Judicial"
                control={form.control}
                options={optionsLawsuit}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 form-group">
              <Input
                name="baixa"
                label="Nº Baixa"
                control={form.control}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Input
                name="installment_id"
                label="Nº Lançamento"
                control={form.control}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <Input
                name="remessa"
                label="Cod Remessa"
                control={form.control}
                errors={errors}
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <CalendarInput
                name="period_start"
                control={form.control}
                errors={errors}
                label="Data início"
              />
            </div>
            <div className="col-xl-3 col-md-4 mb-3 form-group">
              <CalendarInput
                name="period_end"
                control={form.control}
                errors={errors}
                label="Data Fim"
              />
            </div>
            <div className="d-flex justify-content-end my-2">
              <div
                className={`${
                  window.innerWidth <= 992 ? "w-25" : "col-md-1"
                } text-center`}
              >
                <Button
                  type="submit"
                  icon="fa fa-search"
                  aria-label="Search"
                  loading={loading}
                  className={`rounded-3 py-2.5 w-100`}
                />
              </div>
            </div>
          </form>
        </Panel>
      </div>

      {dataTable.length > 0 && (
        <div className="row">
          <div className="col-12">
            <Panel>
              <TableAccount
                key={`table-${JSON.stringify(filter)}-${paginatorState.page}`}
                dataTable={dataTable}
                isLoading={loading}
                totalRecords={paginatorState.totalRecords}
                rows={paginatorState.rows}
                first={paginatorState.first}
                onPageChange={handlePageChange}
              />
              <Paginator
                first={paginatorState.first}
                rows={paginatorState.rows}
                totalRecords={paginatorState.totalRecords}
                onPageChange={handlePageChange}
              />

              <div
                style={{
                  padding: "1rem",
                  borderTop: "1px solid #ddd",
                  marginTop: "1rem",
                }}
              >
                {totals && (
                  <>
                    <div
                      style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}
                    >
                      <div>
                        <strong>Total a receber:</strong>{" "}
                        {formatCurrency(Number(totals.total_full) || 0)}
                      </div>
                      <div>
                        <strong>Total s/multa e juros:</strong>{" "}
                        {formatCurrency(Number(totals.value) || 0)}
                      </div>
                      
                      <div>
                        <strong>Parcelas:</strong> {totals.instalments || 0}
                      </div>
                      <div>
                        <strong>Valor Recebido:</strong>{" "}
                        {formatCurrency(Number(totals.payment_value) || 0)}
                      </div>
                      <div>
                        <strong>Multa:</strong>{" "}
                        {formatCurrency(Number(totals.fine) || 0)}
                      </div>
                      <div>
                        <strong>Honorários:</strong>{" "}
                        {formatCurrency(Number(totals.honorary) || 0)}
                      </div>
                      <div>
                        <strong>Desconto:</strong>{" "}
                        {formatCurrency(Number(totals.discount) || 0)}
                      </div>
                      <div>
                        <strong>Juros:</strong>{" "}
                        {formatCurrency(Number(totals.interest) || 0)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountReceivable;
